import { supabase } from "./supabase";

// In production the admin SPA lives on a different origin from the
// storefront API, so prefix all API paths with the storefront URL.
// In dev, Vite proxies /api/admin/* so no prefix is needed.
const API_BASE = import.meta.env.VITE_ADMIN_API_URL ?? "";

async function adminFetch<T>(path: string): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }

  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Admin API ${res.status}: ${body}`);
  }

  return res.json() as Promise<T>;
}

async function adminRequest<T>(
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown
): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      ...(body !== undefined && { "Content-Type": "application/json" }),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text();
    try {
      const parsed = JSON.parse(text) as { error?: string };
      throw new Error(parsed.error ?? `Admin API ${res.status}: ${text}`);
    } catch {
      throw new Error(`Admin API ${res.status}: ${text}`);
    }
  }

  return res.json() as Promise<T>;
}

async function adminPost<T>(path: string, body: unknown): Promise<T> {
  return adminRequest<T>("POST", path, body);
}

async function adminPatch<T>(path: string, body: unknown): Promise<T> {
  return adminRequest<T>("PATCH", path, body);
}

// ── Health: Sentry summary ───────────────────────────────────────

export interface SentryIssue {
  id: string;
  shortId: string;
  title: string;
  culprit: string | null;
  level: string;
  status: string;
  count: string;
  userCount: number;
  firstSeen: string;
  lastSeen: string;
  permalink: string;
}

export interface SentryProjectSummary {
  slug: string;
  unresolvedCount: number | null;
  eventsLast24h: number | null;
  recentIssues: SentryIssue[];
  issuesUrl: string;
  error: string | null;
}

export interface SentrySummaryResponse {
  org: string | null;
  configured: boolean;
  projects: SentryProjectSummary[];
  fetchedAt: string;
}

export async function fetchSentrySummary(): Promise<SentrySummaryResponse> {
  return adminFetch<SentrySummaryResponse>("/api/admin/sentry/summary");
}

// ── Platform settings ────────────────────────────────────────────

// Mirrors the storefront's PlatformSettings. Only two settings survive V2: the
// P2P ones (fees, shipping, payouts, listing moderation) and the signup gate
// configure things that no longer exist. Their columns remain in the database
// but nothing reads them.
export interface PlatformSettings {
  maintenance_mode: boolean;
  ai_analyze_model_id: string | null;
  /** Site-wide Associates tracking id. Per-product ids live in featured_overrides. */
  amazon_associate_tag: string | null;
}

// ── Featured products (hand-picked home page list) ───────────────

/**
 * One pick, joined from the storefront's code-defined list and its override row.
 *
 * The products themselves are not in a table — they live in
 * `storefront/src/lib/catalog/featured.ts` — so this is read-only except for
 * `enabled` and `associateTag`, which are the two things an operator can change
 * without a deploy.
 */
export interface FeaturedAdminRow {
  asin: string;
  title: string;
  brand: string | null;
  categoryId: string;
  enabled: boolean;
  associateTag: string | null;
  hasOverride: boolean;
}

export async function fetchFeaturedProducts(): Promise<FeaturedAdminRow[]> {
  const res = await adminFetch<{ products: FeaturedAdminRow[] }>(
    "/api/admin/featured"
  );
  return res.products;
}

export async function updateFeaturedProduct(input: {
  asin: string;
  enabled?: boolean;
  associateTag?: string | null;
}): Promise<{ asin: string; enabled: boolean; associateTag: string | null }> {
  return adminPatch("/api/admin/featured", input);
}

// ── Contact messages ─────────────────────────────────────────────

export interface ContactMessageRow {
  id: string;
  name: string;
  email: string;
  message: string;
  createdAt: string;
  handledAt: string | null;
}

export interface ContactMessagesResponse {
  messages: ContactMessageRow[];
  total: number;
  unhandled: number;
}

export async function fetchContactMessages(
  filter: "unhandled" | "all" = "unhandled"
): Promise<ContactMessagesResponse> {
  return adminFetch<ContactMessagesResponse>(
    `/api/admin/contact?filter=${filter}`
  );
}

export async function setContactMessageHandled(input: {
  id: string;
  handled: boolean;
}): Promise<{ id: string; handledAt: string | null }> {
  return adminPatch("/api/admin/contact", input);
}

export async function fetchPlatformSettings(): Promise<PlatformSettings> {
  return adminFetch<PlatformSettings>("/api/admin/settings");
}

export async function updatePlatformSettings(
  patch: Partial<PlatformSettings>
): Promise<PlatformSettings> {
  return adminPatch<PlatformSettings>("/api/admin/settings", patch);
}

// ── AI Usage ─────────────────────────────────────────────────────

export interface AiUsageSummary {
  mtd: { cost_usd: number; calls: number; start_at: string };
  last_30d: { cost_usd: number; calls: number };
  last_7d: { cost_usd: number; calls: number };
  last_24h: { cost_usd: number; calls: number };
  by_day_30d: { date: string; cost_usd: number; calls: number }[];
  by_status_30d: { status: string; count: number }[];
  top_users_30d: {
    user_id: string | null;
    display_name: string | null;
    calls: number;
    cost_usd: number;
  }[];
  top_ips_30d: { ip: string | null; calls: number; cost_usd: number }[];
  model: string | null;
  burn: {
    daily_avg_7d: number;
    daily_avg_30d: number;
    projected_mtd: number;
  };
  avg_cost_per_call_30d: number;
  avg_images_per_call_30d: number;
  cache_30d: {
    hit_rate: number;
    cache_read_tokens: number;
    uncached_input_tokens: number;
    cache_creation_tokens: number;
  };
  models: {
    active_id: string;
    is_override: boolean;
    catalog: {
      id: string;
      family: string;
      inputPerMTok: number;
      outputPerMTok: number;
      cacheReadPerMTok: number;
      cacheWritePerMTok: number;
      tagline: string | null;
      description: string | null;
      contextWindow: number | null;
      maxOutputTokens: number | null;
      vision: boolean | null;
      toolUse: boolean | null;
      knowledgeCutoff: string | null;
      releaseDate: string | null;
    }[];
    discovered_unknown: {
      id: string;
      family: string | null;
      version: string | null;
      first_seen_at: string;
    }[];
    family_upgrades: {
      id: string;
      display_name: string | null;
      family: string;
      version: string;
      snapshot: string | null;
      first_seen_at: string;
    }[];
    last_sync_at: string | null;
  };
  anomaly: {
    tier: "normal" | "warning" | "alert";
    spike_ratio: number;
    reason: string;
  };
  rate_limit: {
    tpm_remaining: number | null;
    tpm_limit: number | null;
    rpm_remaining: number | null;
    rpm_limit: number | null;
    reset_at: string | null;
    captured_at: string | null;
    tpm_pct_remaining: number | null;
    rpm_pct_remaining: number | null;
  } | null;
}

export interface AiUsageLogRow {
  id: string;
  created_at: string;
  user_id: string | null;
  user_display_name: string | null;
  ip: string | null;
  user_agent: string | null;
  image_count: number;
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  cost_usd: number;
  duration_ms: number | null;
  status: string;
  error_message: string | null;
}

export async function fetchAiUsageSummary(): Promise<AiUsageSummary> {
  return adminFetch<AiUsageSummary>("/api/admin/ai-usage");
}

export interface ModelSyncResult {
  skipped?: boolean;
  reason?: string;
  rows_upserted?: number;
  new_models?: string[];
  error?: string;
}

export async function syncAnthropicModels(): Promise<ModelSyncResult> {
  return adminPost<ModelSyncResult>("/api/admin/ai-usage/sync-models", {});
}

export async function setActiveAnalyzeModel(
  modelId: string | null
): Promise<PlatformSettings> {
  return updatePlatformSettings({ ai_analyze_model_id: modelId });
}

export async function fetchAiUsageLogs(params?: {
  limit?: number;
  status?: string;
}): Promise<{ rows: AiUsageLogRow[] }> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.status) qs.set("status", params.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return adminFetch<{ rows: AiUsageLogRow[] }>(`/api/admin/ai-usage/logs${suffix}`);
}

// ── Cron runs ────────────────────────────────────────────────────

export type CronRunStatus = "running" | "success" | "error";

export interface CronRunRow {
  id: string;
  job_name: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  status: CronRunStatus;
  error_message: string | null;
  details: Record<string, unknown>;
}

export interface CronJobSummary {
  name: string;
  lastRun: CronRunRow | null;
  successCount7d: number;
  errorCount7d: number;
  avgDurationMs7d: number | null;
}

export interface CronRunsResponse {
  jobs: CronJobSummary[];
  recent: CronRunRow[];
  fetchedAt: string;
}

export async function fetchCronRuns(): Promise<CronRunsResponse> {
  return adminFetch<CronRunsResponse>("/api/admin/cron/runs");
}

// ── Catalog operations (V2) ──────────────────────────────────────

export interface CatalogStats {
  sources: {
    id: string;
    label: string;
    kind: string;
    retention: string;
    offer_ttl_seconds: number | null;
    enabled: boolean;
  }[];
  quota: {
    callsLast24h: number;
    allowance: number;
    percentUsed: number;
    failedLast24h: number;
    byOperation: Record<string, { calls: number; items: number; failed: number }>;
  };
  catalog: { products: number; offers: number; pendingMergeReviews: number };
  recentRuns: {
    job_name: string;
    status: string;
    started_at: string;
    duration_ms: number | null;
    details: Record<string, unknown> | null;
    error_message: string | null;
  }[];
}

export interface MergeCandidateProduct {
  id: string;
  slug: string | null;
  title: string;
  brand: string | null;
  mpn: string | null;
  upc: string | null;
  category_id: string | null;
}

export interface MergeCandidate {
  id: string;
  reason: "mpn_match_upc_conflict" | "mpn_match_upc_missing" | "title_near_match";
  similarity: number | null;
  status: "pending" | "merged" | "rejected";
  created_at: string;
  a: MergeCandidateProduct;
  b: MergeCandidateProduct;
}

export async function fetchCatalogStats(): Promise<CatalogStats> {
  return adminFetch<CatalogStats>("/api/admin/catalog/stats");
}

export async function fetchMergeCandidates(
  status: "pending" | "merged" | "rejected" = "pending"
): Promise<MergeCandidate[]> {
  const res = await adminFetch<{ candidates: MergeCandidate[] }>(
    `/api/admin/catalog/merge-candidates?status=${status}`
  );
  return res.candidates;
}

export async function resolveMergeCandidate(
  id: string,
  action: "merge" | "reject"
): Promise<{ ok: boolean }> {
  return adminPatch<{ ok: boolean }>(
    `/api/admin/catalog/merge-candidates/${id}`,
    { action }
  );
}
